/**
 * Wild Network — brand palette (3×8: neutrals, earth, pastels).
 * Hex values match the brand swatch sheet; adjust when you export exact values from design tools.
 *
 * Raw brand swatches for TS consumers (map charts). UI chrome should use the `--wn-*` tokens in
 * `wildNetworkTheme.css`, which is the source of truth for the web app's colours.
 *
 * Use `wildNetwork` / `wildNetworkMoisture` / `wildNetworkUserLocation` in components, or CSS vars from `wildNetworkTheme.css`.
 */

export const wildNetworkPalette = {
  neutral: {
    /** Light: body / heading text (legacy light spec); dark overridden in CSS */
    ink: '#282d2a',
    inkDeep: '#1a1b1b',
    gray8: '#2e2e2e',
    gray7: '#4a4a4a',
    gray6: '#6b6b6b',
    gray5: '#8f8f8f',
    gray4: '#b8b8b8',
    /** App background (parchment, light — typography guide cream) */
    paper: '#f5f2e8',
    white: '#ffffff',
    textMuted: '#8a9c84',
    borderSubtle: '#d4cfc3',
    pageSubtle: '#f0ebe0'
  },
  earth: {
    forest: '#4d675a',
    forestDark: '#3e5348',
    forestMuted: '#516b5e',
    forestHover: '#3e5348',
    terracotta: '#a85a42',
    terracottaDeep: '#8c3d2e',
    amber: '#c2783d',
    olive: '#7a6b3d',
    bark: '#6b5344',
    slateBlue: '#3d5466',
    sky: '#5a8cae',
    purple: '#5c4d6b'
  },
  tint: {
    mint: '#d8e5da',
    rose: '#f0e4e0',
    peach: '#f5e8dc',
    butter: '#f3ead0',
    sand: '#ebe3d6',
    slateMist: '#cfd8e0',
    skyWash: '#d5e8f2',
    lilac: '#e8e0f0'
  }
} as const;

/**
 * Mint-tinted surfaces / borders aligned with Radix custom green scale (map theme).
 * Used by designTokens for forms and cards until components migrate fully to earth + sand.
 */
export const wildNetworkGreenUi = {
  surface1: '#ffffff',
  surface2: '#f5f2e8',
  surface3: '#f0ebe0',
  surface4: '#e8e0d4',
  border: '#d4cfc3',
  borderHover: '#c5c4b8',
  borderLight: '#d4cfc3',
  borderDark: '#a8b5ad',
  wash: '#a2beb0'
} as const;

/** User location / Favorites place drawer — buttons, type, chips. */
export const wildNetworkUserLocation = {
  text: wildNetworkPalette.neutral.ink,
  textMuted: '#6d6f73',
  textSubtle: '#9a9a95',
  primary: wildNetworkPalette.earth.forest,
  primaryHover: wildNetworkPalette.earth.forestHover,
  white: wildNetworkPalette.neutral.white,
  chip: '#e7e9e4',
  skeletonFrom: '#e4e4de',
  skeletonTo: '#f2f2ed',
  landUse: '#597565',
  headingStrong: '#2b2b2b',
  /** Soil moisture row — dry (below 40%) */
  soilMoistureLow: '#834844',
  /** Soil row — placeholder / no data */
  soilMuted: '#9e9e9e'
} as const;

/** Moisture card + YTD water chart (GrowingSeasonPrecipChart). */
const wildNetworkMoisture = {
  ui: {
    cardBg: '#faf9f6',
    cardBorder: '#e8e4dc',
    paperElevated: '#fbfbfa',
    tipBg: wildNetworkPalette.neutral.white,
    tipBorder: '#e8ebe9',
    eyebrow: '#7a8a7f',
    title: wildNetworkPalette.neutral.ink,
    subtitle: '#5c665e',
    labelMuted: '#8a9a8f',
    bodyMuted: '#5c665e',
    disclaimer: '#a8b0a8',
    warning: '#b45309',
    typicalValue: '#3d4a42',
    buttonBorder: '#d8ddd9',
    buttonFg: '#5c665e',
    shimmerLo: '#ece8e0',
    shimmerHi: '#f5f3f0'
  },
  badge: {
    drought: {
      bg: '#faf0ed',
      border: '#e8c4bc',
      color: wildNetworkPalette.earth.terracottaDeep
    },
    dry: {
      bg: '#faf6f0',
      border: '#edd8c0',
      color: '#9a5a28'
    },
    typical: {
      bg: '#f0f7f2',
      border: '#c5d9cc',
      color: wildNetworkPalette.earth.forest
    },
    wet: {
      bg: '#eef6fc',
      border: '#c5d9eb',
      color: wildNetworkPalette.earth.slateBlue
    },
    very_wet: {
      bg: '#eef6f7',
      border: '#b8dce3',
      color: '#2e6b7a'
    },
    unknown: {
      bg: wildNetworkPalette.neutral.paper,
      border: '#e2e8e0',
      color: '#5c665e'
    }
  },
  line: {
    drought: wildNetworkPalette.earth.terracotta,
    dry: wildNetworkPalette.earth.amber,
    typical: wildNetworkPalette.earth.forest,
    wet: wildNetworkPalette.earth.sky,
    very_wet: wildNetworkPalette.earth.slateBlue,
    default: '#6b7a72'
  },
  accent: {
    drought: wildNetworkPalette.earth.terracottaDeep,
    dry: '#b45309',
    typical: wildNetworkPalette.earth.forest,
    wet: '#2d6a8a',
    very_wet: wildNetworkPalette.earth.slateBlue,
    default: wildNetworkPalette.neutral.ink
  },
  segment: [
    wildNetworkPalette.tint.rose,
    wildNetworkPalette.tint.peach,
    wildNetworkPalette.tint.mint,
    wildNetworkPalette.tint.skyWash,
    wildNetworkPalette.tint.lilac
  ],
  chart: {
    avgStroke: '#6b7a72',
    grid: '#eef0ee',
    axis: '#9ca3af',
    hoverLine: '#d4d4d8',
    tooltipBorder: '#e5e7eb',
    tooltipTitle: '#1e293b'
  },
  shadow: {
    card: '0 1px 2px rgba(45, 42, 38, 0.06)',
    tooltip: '0 4px 12px rgba(45, 42, 38, 0.08)'
  }
} as const;

const wildNetworkFont = {
  /** UI / body — Cabin (matches mobile) */
  ui: '"Cabin", sans-serif',
  /** Labels, data, coordinates */
  mono: '"DM Mono", monospace',
  editorial: '"Lora", serif'
} as const;

/**
 * “In season” live indicator beside species names (Field Guide grid, map search, mobile).
 * Canonical hex — mirrored as `--wn-in-season-dot-*` in `wildNetworkTheme.css` and `AppColors` on mobile.
 */
export const wildNetworkInSeasonDot = {
  sizePx: 8,
  borderRadius: '50%',
  bg: '#5dbb7d'
} as const;

/** Convenience bundle for imports. */
export const wildNetwork = {
  palette: wildNetworkPalette,
  greenUi: wildNetworkGreenUi,
  userLocation: wildNetworkUserLocation,
  moisture: wildNetworkMoisture,
  font: wildNetworkFont,
  inSeasonDot: wildNetworkInSeasonDot
} as const;
